import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, MessageCircle, X, CalendarHeart, Package, Store, Star } from "lucide-react";
import Seo from "../components/Seo";
import { PROVINCES, CITIES_BY_PROVINCE, DISTRICTS_BY_CITY } from "../lib/location-data";
import { formatIDR } from "../lib/format";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import { VENDORS } from "../lib/vendors";

const publicApi = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL + "/api" });
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);
const SALES_WA = "628112225912";
const fmtIDR = (n) => "Rp." + Number(n||0).toLocaleString("id-ID");

const LAYANAN_OPTIONS = [
  {
    key: "all_package",
    title: "Paket All Package (Wedding DWG)",
    desc: "Paket bundling lengkap CV Dessy Wedding Group — MUA + Dekorasi + Foto/Video + WO dalam satu paket.",
    icon: Package,
  },
  {
    key: "satuan",
    title: "Vendor Satuan",
    desc: "Pilih vendor satu per satu sesuai kebutuhan (MUA saja, Dekor saja, Foto saja, dll).",
    icon: Store,
  },
];

const PAKET_TYPES = [
  {
    key: "akad",
    title: "Akad",
    desc: "Paket khusus prosesi akad nikah — hemat & fokus.",
    families: ["AKAD PACKAGE"],
  },
  {
    key: "essential",
    title: "Essential (Intimate)",
    desc: "Wedding intimate atau rumahan sederhana — All-in praktis lengkap.",
    families: ["ESSENTIAL WEDDING"],
  },
  {
    key: "resepsi",
    title: "Resepsi",
    desc: "Paket resepsi gedung (Indoor) atau tenda (Outdoor) skala lengkap.",
    families: ["INDOOR WEDDING", "OUTDOOR WEDDING"],
  },
];

// Guest ranges & budget ranges are calibrated to the actual DWG 2025 pricelist per package family.
// AKAD:  Rp7,7jt – Rp9,35jt (tanpa jumlah tamu — prosesi akad tidak dibatasi jumlah tamu resepsi)
// ESSENTIAL (Intimate): Rp15jt – Rp16,5jt, max 300 tamu
// RESEPSI (Indoor+Outdoor): Rp20jt – Rp75jt, 100–1000+ tamu
const DEFAULT_GUESTS = ["100–300 orang", "300–500 orang", "500–800 orang", "800–1.000 orang", "1.000+ orang"];
const DEFAULT_BUDGETS = ["Di bawah Rp10 juta", "Rp10–20 juta", "Rp20–35 juta", "Rp35–50 juta", "Rp50–75 juta", "Rp75 juta+"];

const BUDGETS_BY_TYPE = {
  akad: ["Di bawah Rp8 juta", "Rp8–10 juta", "Rp10 juta+"],
  essential: ["Di bawah Rp15 juta", "Rp15–17 juta", "Rp17 juta+"],
  resepsi: ["Rp20–25 juta", "Rp25–35 juta", "Rp35–50 juta", "Rp50–75 juta", "Rp75 juta+"],
};

const GUESTS_BY_TYPE = {
  akad: [], // dilewati — tidak relevan untuk prosesi akad
  essential: ["100–300 orang"], // intimate/rumahan, dibatasi 300 tamu
  resepsi: DEFAULT_GUESTS,
};

const budgetsForStep = (data) => (data.layanan === "all_package" && BUDGETS_BY_TYPE[data.paket_type]) || DEFAULT_BUDGETS;
const guestsForStep = (data) => (data.layanan === "all_package" && GUESTS_BY_TYPE[data.paket_type]) || DEFAULT_GUESTS;

const parseBudgetMax = (b) => {
  if (!b) return Infinity;
  if (b.trim().endsWith("juta+")) return Infinity;
  const nums = (b.match(/\d+/g) || []).map(Number);
  if (nums.length === 0) return Infinity;
  if (b.startsWith("Di bawah")) return nums[0] * 1_000_000;
  return nums[nums.length - 1] * 1_000_000;
};

const flowFor = (layanan, hasProduct, paketType) => {
  if (hasProduct) return ["guests", "budget", "location", "customer", "result"];
  if (layanan === "all_package") {
    const base = ["layanan", "paket_type"];
    if (paketType !== "akad") base.push("guests"); // Akad tidak minta jumlah tamu
    return [...base, "budget", "location", "customer", "result"];
  }
  if (layanan === "satuan") return ["layanan", "pick_packages", "location", "customer", "result"];
  return ["layanan", "guests", "budget", "location", "customer", "result"];
};

export default function Consultation() {
  const nav = useNavigate();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const initial = useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    return {
      product_id: sp.get("product_id") || "",
      layanan: sp.get("layanan") || "",
      paket_type: sp.get("paket_type") || "",
      vendor_slug: sp.get("vendor_slug") || sp.get("vendor") || "",
      selected_products: [], // { product_id, name, brand, price, division, image }
      guests: sp.get("guests") || "",
      budget: sp.get("budget") || "",
      province: sp.get("province") || "Jawa Barat",
      city: sp.get("city") || "", district: sp.get("district") || "",
      venue: sp.get("venue") || "", name: "", event_date: sp.get("event_date") || "",
      whatsapp: sp.get("whatsapp") || "", email: sp.get("email") || "",
    };
  }, []);
  const [data, setData] = useState(initial);
  const [catalogVendor, setCatalogVendor] = useState([]); // list of non-bundling published products
  const [pickerFilter, setPickerFilter] = useState({ division: "all", q: "" });

  useEffect(() => {
    // Preload the vendor catalog once so the multi-package picker is instant
    publicApi.get("/public/catalog", { params: { bundling: false } })
      .then(({ data }) => setCatalogVendor(data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (initial.product_id) {
      publicApi.get(`/public/catalog/${initial.product_id}`)
        .then(({ data: p }) => setSelectedProduct(p))
        .catch(() => toast.error("Produk tidak ditemukan"));
    }
  }, [initial.product_id]);

  const hasProduct = !!initial.product_id;
  const [step, setStep] = useState(hasProduct ? "guests" : "layanan");
  const [rec, setRec] = useState(null);
  const [leadId, setLeadId] = useState(null);
  const [floatVisible, setFloatVisible] = useState(false);
  const [floatChip, setFloatChip] = useState(false);

  const flow = useMemo(() => flowFor(data.layanan, hasProduct, data.paket_type), [data.layanan, hasProduct, data.paket_type]);
  const stepIdx = Math.max(flow.indexOf(step), 0);

  const setField = (k, v) => setData((p) => { const n = { ...p, [k]: v }; setRec(null); return n; });

  const next = () => setStep(flow[Math.min(stepIdx + 1, flow.length - 1)]);
  const back = () => {
    if (step === "layanan") return;
    const prev = flow[Math.max(stepIdx - 1, 0)];
    setStep(prev);
  };

  // Recommendation using live catalog
  useEffect(() => {
    if (step !== "result" || rec) return;
    if (selectedProduct) {
      const price = selectedProduct.promo_active ? selectedProduct.promo_price : selectedProduct.normal_price;
      setRec({
        product_id: selectedProduct.product_id,
        name: selectedProduct.product_name, brand: selectedProduct.brand,
        price, image: resolveImg(selectedProduct.card_image),
        note: selectedProduct.short_description || "",
      });
      return;
    }

    // Vendor Satuan multi-pick → "Paket Custom" recommendation
    if (data.layanan === "satuan" && (data.selected_products?.length || 0) > 0) {
      const items = data.selected_products;
      const total = items.reduce((a, p) => a + (p.price || 0), 0);
      setRec({
        name: "Paket Custom",
        price: total,
        brand: `${items.length} paket dipilih`,
        note: "Total gabungan — tim kami akan mengonfirmasi ketersediaan & susunan final.",
        items,
      });
      publicApi.post("/public/leads", {
        name: data.name, event_date: data.event_date || null,
        whatsapp: data.whatsapp, email: data.email,
        event_type: "Vendor Satuan (Paket Custom)",
        guest_count: data.guests || "", budget: fmtIDR(total),
        province: data.province, city: data.city, district: data.district, venue: data.venue,
        services: Array.from(new Set(items.map(p => p.brand).filter(Boolean))),
        recommended_package: `Paket Custom (${items.length} item)`,
        recommended_price: total,
        product_id: "", product_family: "custom",
        product_version: "custom", source: "consultation",
        selected_products: items.map(p => ({ product_id: p.product_id, name: p.name, price: p.price, brand: p.brand })),
      }).then(({ data: d }) => { setLeadId(d.id); setFloatVisible(true); }).catch(() => {});
      return;
    }
    const params = {};
    if (data.layanan === "all_package") params.bundling = true;
    if (data.layanan === "satuan" && data.vendor_slug) {
      const v = VENDORS.find(x => x.slug === data.vendor_slug);
      if (v) { params.division = v.division_id; params.bundling = false; }
    }
    const budgetMax = parseBudgetMax(data.budget);
    if (budgetMax !== Infinity) params.max_price = budgetMax;

    // Determine which families to filter for (all_package + paket_type)
    const targetFamilies = (data.layanan === "all_package" && data.paket_type)
      ? (PAKET_TYPES.find(pt => pt.key === data.paket_type)?.families || [])
      : [];

    const pickAndSet = (list) => {
      list.sort((a, b) => (a.current_price ?? a.normal_price) - (b.current_price ?? b.normal_price));
      const inBudget = list.filter(x => (x.current_price ?? x.normal_price) <= budgetMax);
      const p = inBudget[inBudget.length - 1] || list[0];
      if (!p) {
        setRec({ name: "Konsultasi Langsung", price: 0, note: "Tim kami akan menghubungi Anda untuk penawaran khusus." });
      } else {
        setRec({
          product_id: p.product_id, name: p.product_name, brand: p.brand,
          price: p.current_price ?? p.normal_price,
          image: resolveImg(p.card_image), note: p.short_description || "",
        });
      }
      publicApi.post("/public/leads", {
        name: data.name, event_date: data.event_date || null,
        whatsapp: data.whatsapp, email: data.email,
        event_type: data.layanan === "satuan" ? `Satuan/${data.vendor_slug}` : "Paket All Package",
        guest_count: data.guests, budget: data.budget,
        province: data.province, city: data.city, district: data.district, venue: data.venue,
        services: data.vendor_slug ? [VENDORS.find(v=>v.slug===data.vendor_slug)?.name || ""] : [],
        recommended_package: p?.product_name || "",
        recommended_price: p ? (p.current_price ?? p.normal_price) : 0,
        product_id: p?.product_id || "", product_family: p?.product_family || "",
        product_version: p?.source_version || "", source: "consultation",
      }).then(({ data: d }) => { setLeadId(d.id); setFloatVisible(true); }).catch(() => {});
    };

    publicApi.get("/public/catalog", { params }).then(({ data: list }) => {
      let items = list || [];
      // Client-side family filter for paket_type (backend supports single `family`; we may need multiple)
      if (targetFamilies.length > 0) {
        items = items.filter(x => targetFamilies.includes(x.product_family));
      }
      if (items.length === 0 && params.max_price) {
        const fallback = { ...params }; delete fallback.max_price;
        return publicApi.get("/public/catalog", { params: fallback }).then(({ data: l2 }) => {
          let l3 = l2 || [];
          if (targetFamilies.length > 0) l3 = l3.filter(x => targetFamilies.includes(x.product_family));
          pickAndSet(l3);
        });
      }
      pickAndSet(items);
    }).catch(() => setRec({ name: "Konsultasi Langsung", price: 0, note: "Tim kami akan menghubungi Anda untuk penawaran khusus." }));
  }, [step, rec, data, selectedProduct]);

  const waMessage = useMemo(() => {
    const vendorName = data.vendor_slug ? (VENDORS.find(v=>v.slug===data.vendor_slug)?.name || "") : "";
    const layananLabel = data.layanan === "all_package" ? "Paket All Package (Wedding DWG)"
      : data.layanan === "satuan" ? (data.selected_products?.length ? "Vendor Satuan — Paket Custom" : `Vendor Satuan — ${vendorName}`) : "-";
    const items = data.selected_products || [];
    const itemsBlock = items.length > 0
      ? `\nPaket Dipilih:\n${items.map((p, i) => `${i+1}. ${p.name}${p.brand ? " ("+p.brand+")" : ""} — ${fmtIDR(p.price)}`).join("\n")}\nTotal Sementara: ${fmtIDR(items.reduce((a,p)=>a+(p.price||0),0))}`
      : "";
    return `Halo DWG, saya ingin konsultasi.\n\nNama: ${data.name}\nTanggal Acara: ${data.event_date || "-"}\nJenis Layanan: ${layananLabel}\n${data.guests ? `Jumlah Tamu: ${data.guests}\n` : ""}${data.budget ? `Budget: ${data.budget}\n` : ""}Lokasi: ${[data.district, data.city, data.province].filter(Boolean).join(", ")}\n${data.venue ? `Venue: ${data.venue}\n` : ""}${itemsBlock}${!itemsBlock ? `Rekomendasi: ${rec?.name || "-"}${rec?.price ? ` — ${fmtIDR(rec.price)}` : ""}` : ""}`;
  }, [data, rec]);

  const waHref = useMemo(
    () => `https://wa.me/${SALES_WA}?text=${encodeURIComponent(waMessage)}`,
    [waMessage]
  );

  // Fire-and-forget tracking (Safari blocks window.open after await, so we
  // rely on the anchor's native navigation and only ping the API in parallel).
  const trackWA = () => {
    if (leadId) { publicApi.put(`/public/leads/${leadId}/whatsapp`).catch(() => {}); }
  };

  const canContinue = () => {
    if (step === "layanan") return !!data.layanan;
    if (step === "paket_type") return !!data.paket_type;
    if (step === "vendor") return !!data.vendor_slug;
    if (step === "pick_packages") return (data.selected_products?.length || 0) > 0;
    if (step === "guests") return !!data.guests;
    if (step === "budget") return !!data.budget;
    if (step === "location") return data.province && data.city;
    if (step === "customer") return data.name && data.event_date && data.whatsapp;
    return true;
  };

  const totalSteps = flow.length - 1;
  const progress = Math.min(((stepIdx) / Math.max(totalSteps - 1, 1)) * 100, 100);

  return (
    <div className="min-h-screen bg-[#F6F0EC] w-full max-w-full overflow-x-hidden" style={{ fontFamily: "'DM Sans','Inter',sans-serif" }} data-testid="consultation-page" data-public-page="true">
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:wght@600;700&display=swap" rel="stylesheet"/>
      <style>{`.font-serif{font-family:'Playfair Display',serif} .card-elev{box-shadow:0 8px 32px -12px rgba(139,38,45,.12),0 2px 8px -2px rgba(0,0,0,.05)}`}</style>

      <Seo title="Konsultasi Wedding Gratis" path="/consultation"
        description="Konsultasi gratis dengan tim Dessy Wedding Group. Ceritakan tanggal, jumlah tamu & budget — kami rekomendasikan paket wedding, akad, Intimade atau khitan yang paling pas." />
      <PublicNav />

      <div className="max-w-3xl mx-auto px-3 sm:px-6 py-6 md:py-12">
        <div className="mb-4 flex items-center justify-between">
          <button onClick={() => nav(-1)} className="text-xs text-[#706762] hover:text-[#8B262D] flex items-center gap-1" data-testid="btn-back-consult"><ArrowLeft className="w-3.5 h-3.5"/> Kembali</button>
          <div className="text-[10px] uppercase tracking-widest text-[#706762]">Dessy Wedding Group</div>
        </div>

        <div className="bg-[#FFFDF9] rounded-2xl p-5 md:p-8 card-elev" data-testid="consult-card">
          {selectedProduct && (
            <div className="mb-5 p-4 rounded-xl bg-[#EFE6D3] border border-[#C4A57B]" data-testid="consult-product-context">
              <div className="text-[10px] uppercase tracking-widest text-[#8B5A2A] font-semibold mb-1">Paket yang sedang dibahas</div>
              <div className="font-serif text-xl text-[#1E1611]">{selectedProduct.product_name}</div>
              <div className="flex items-center gap-3 mt-1 text-sm">
                <span className="text-[#5C4A3D] italic">{selectedProduct.brand}</span>
                <span className="font-mono text-[#8B262D] font-semibold">
                  {formatIDR(selectedProduct.promo_active ? selectedProduct.promo_price : selectedProduct.normal_price)}
                </span>
              </div>
            </div>
          )}
          {step !== "result" && (
            <>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-[#8B262D] font-semibold">Konsultasi Online</div>
                <div className="text-xs text-[#706762]" data-testid="step-indicator">{stepIdx + 1} / {totalSteps}</div>
              </div>
              <div className="h-1 bg-[#F6F0EC] rounded-full mb-6 overflow-hidden">
                <div className="h-full bg-[#8B262D] transition-all duration-500" style={{ width: `${progress}%` }}/>
              </div>
            </>
          )}

          {step === "layanan" && (
            <StepBlock title="Pilih jenis layanan" hint="Mau paket bundling lengkap atau vendor satuan?">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {LAYANAN_OPTIONS.map((t) => {
                  const Icon = t.icon;
                  const active = data.layanan === t.key;
                  return (
                    <button key={t.key} onClick={() => { setField("layanan", t.key); setField("vendor_slug", ""); }}
                      className={`p-5 rounded-xl border text-left transition-all ${active ? "bg-[#8B262D] text-white border-[#8B262D]" : "bg-white border-[#E5D9C4] hover:border-[#B87C80]"}`}
                      data-testid={`layanan-${t.key}`}>
                      <Icon className={`w-6 h-6 mb-2 ${active ? "text-white" : "text-[#8B262D]"}`}/>
                      <div className={`font-semibold ${active ? "text-white" : "text-[#1F1A19]"}`}>{t.title}</div>
                      <div className={`text-xs mt-1.5 ${active ? "text-white/85" : "text-[#706762]"}`}>{t.desc}</div>
                    </button>
                  );
                })}
              </div>
            </StepBlock>
          )}

          {step === "paket_type" && (
            <StepBlock title="Pilih jenis paket wedding" hint="Sesuaikan dengan acara yang ingin dibahas.">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {PAKET_TYPES.map((pt) => {
                  const active = data.paket_type === pt.key;
                  return (
                    <button key={pt.key} onClick={() => setData((p) => ({ ...p, paket_type: pt.key, guests: "", budget: "" }))}
                      className={`p-4 rounded-xl border text-left transition-all ${active ? "bg-[#8B262D] text-white border-[#8B262D]" : "bg-white border-[#E5D9C4] hover:border-[#B87C80]"}`}
                      data-testid={`paket-${pt.key}`}>
                      <div className={`font-semibold text-sm ${active ? "text-white" : "text-[#1F1A19]"}`}>{pt.title}</div>
                      <div className={`text-[11px] mt-1 ${active ? "text-white/85" : "text-[#706762]"}`}>{pt.desc}</div>
                    </button>
                  );
                })}
              </div>
            </StepBlock>
          )}

          {step === "pick_packages" && (
            <StepBlock title="Pilih paket yang diinginkan" hint="Pilih satu atau beberapa paket. Total akan digabungkan sebagai Paket Custom.">
              {(() => {
                const items = data.selected_products || [];
                const total = items.reduce((a, p) => a + (p.price || 0), 0);
                const divisions = Array.from(new Set(catalogVendor.map(p => p.division).filter(Boolean)));
                const filtered = catalogVendor.filter(p => {
                  if (pickerFilter.division !== "all" && p.division !== pickerFilter.division) return false;
                  if (pickerFilter.q) {
                    const s = pickerFilter.q.toLowerCase();
                    return (p.product_name || "").toLowerCase().includes(s) || (p.brand || "").toLowerCase().includes(s);
                  }
                  return true;
                });
                const toggle = (p) => {
                  const price = p.current_price ?? (p.promo_active ? p.promo_price : p.normal_price) ?? 0;
                  const existing = items.find(x => x.product_id === p.product_id);
                  const nextList = existing
                    ? items.filter(x => x.product_id !== p.product_id)
                    : [...items, { product_id: p.product_id, name: p.product_name, brand: p.brand, price, division: p.division, image: resolveImg(p.card_image) }];
                  setField("selected_products", nextList);
                };
                const remove = (pid) => setField("selected_products", items.filter(x => x.product_id !== pid));
                return (
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-2 items-center">
                      <select value={pickerFilter.division} onChange={(e)=>setPickerFilter(f=>({...f, division: e.target.value}))}
                        className="h-9 text-xs border border-[#E5D9C4] bg-white rounded-lg px-2" data-testid="picker-filter-division">
                        <option value="all">Semua Vendor</option>
                        {divisions.map(d => {
                          const v = VENDORS.find(x => x.division_id === d);
                          return <option key={d} value={d}>{v?.name || d}</option>;
                        })}
                      </select>
                      <Input placeholder="Cari nama paket..." value={pickerFilter.q}
                        onChange={(e)=>setPickerFilter(f=>({...f, q: e.target.value}))}
                        className="h-9 flex-1 min-w-[180px] bg-white border-[#E5D9C4] text-sm" data-testid="picker-search"/>
                    </div>
                    <div className="max-h-[420px] overflow-y-auto pr-1 space-y-2" data-testid="picker-list">
                      {catalogVendor.length === 0 && <div className="text-sm text-[#706762] text-center py-8">Memuat katalog paket...</div>}
                      {filtered.map((p) => {
                        const active = !!items.find(x => x.product_id === p.product_id);
                        const price = p.current_price ?? (p.promo_active ? p.promo_price : p.normal_price) ?? 0;
                        return (
                          <button key={p.product_id} onClick={() => toggle(p)}
                            className={`w-full text-left p-3 rounded-xl border transition-all flex items-center gap-3 ${active ? "bg-[#8B262D] text-white border-[#8B262D]" : "bg-white border-[#E5D9C4] hover:border-[#B87C80]"}`}
                            data-testid={`pick-${p.product_id}`}>
                            <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${active ? "bg-white border-white" : "border-[#B87C80]"}`}>
                              {active && <span className="text-[#8B262D] text-xs font-bold">✓</span>}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className={`text-sm font-medium truncate ${active ? "text-white" : "text-[#1F1A19]"}`}>{p.product_name}</div>
                              <div className={`text-[11px] truncate ${active ? "text-white/80" : "text-[#706762]"}`}>{p.brand || "-"}</div>
                            </div>
                            <div className={`font-mono text-sm font-semibold shrink-0 ${active ? "text-white" : "text-[#8B262D]"}`}>{fmtIDR(price)}</div>
                          </button>
                        );
                      })}
                      {catalogVendor.length > 0 && filtered.length === 0 && (
                        <div className="text-sm text-[#706762] text-center py-6">Tidak ada paket yang cocok.</div>
                      )}
                    </div>
                    {items.length > 0 && (
                      <div className="sticky bottom-0 bg-[#EFE6D3] border border-[#C4A57B] rounded-xl p-3" data-testid="picker-cart">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-[10px] uppercase tracking-widest text-[#8B5A2A] font-semibold">Paket Custom ({items.length} item)</div>
                          <div className="font-serif text-xl font-semibold text-[#8B262D]" data-testid="picker-total">{fmtIDR(total)}</div>
                        </div>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {items.map((p) => (
                            <div key={p.product_id} className="flex items-center justify-between gap-2 text-xs">
                              <div className="truncate flex-1 text-[#1F1A19]">{p.name}</div>
                              <div className="font-mono text-[#5C4A3D]">{fmtIDR(p.price)}</div>
                              <button onClick={(e)=>{e.stopPropagation(); remove(p.product_id);}}
                                className="text-[#8B262D] hover:text-[#6d1d23] shrink-0" data-testid={`picker-remove-${p.product_id}`}
                                aria-label="Hapus">×</button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </StepBlock>
          )}

          {step === "vendor" && (
            <StepBlock title="Pilih vendor" hint="Pilih 1 vendor dulu — bisa konsultasi ulang untuk vendor lain.">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {VENDORS.filter(v => v.featured).map((v) => (
                  <button key={v.slug} onClick={() => setField("vendor_slug", v.slug)}
                    className={`p-3 rounded-xl border text-left transition-all flex items-center gap-3 ${data.vendor_slug === v.slug ? "bg-[#8B262D] text-white border-[#8B262D]" : "bg-white border-[#E5D9C4] hover:border-[#B87C80]"}`}
                    data-testid={`vendor-pick-${v.slug}`}>
                    <img src={v.image} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0"/>
                    <div>
                      <div className={`text-sm font-medium ${data.vendor_slug === v.slug ? "text-white" : "text-[#1F1A19]"}`}>{v.name}</div>
                      <div className={`text-[11px] ${data.vendor_slug === v.slug ? "text-white/75" : "text-[#706762]"}`}>{v.tagline}</div>
                    </div>
                  </button>
                ))}
              </div>
            </StepBlock>
          )}

          {step === "guests" && (
            <StepBlock title="Berapa perkiraan jumlah tamu?" hint={data.paket_type === "essential" ? "Paket Essential (Intimate) maksimal 300 tamu." : undefined}>
              <div className="grid grid-cols-1 gap-2">
                {guestsForStep(data).map((g) => (
                  <ChoiceRow key={g} active={data.guests === g} onClick={() => setField("guests", g)} label={g} testId={`guest-${g.slice(0,3)}`}/>
                ))}
              </div>
            </StepBlock>
          )}

          {step === "budget" && (
            <StepBlock title="Berapa budget yang disiapkan?" hint={data.layanan === "all_package" && data.paket_type ? `Range harga disesuaikan dengan paket ${PAKET_TYPES.find(pt=>pt.key===data.paket_type)?.title}.` : undefined}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {budgetsForStep(data).map((b) => (
                  <ChoiceRow key={b} active={data.budget === b} onClick={() => setField("budget", b)} label={b} testId={`bud-${b.slice(0,5).replace(/[^\w]/g,'')}`}/>
                ))}
              </div>
            </StepBlock>
          )}

          {step === "location" && (
            <StepBlock title="Di mana acara akan dilaksanakan?">
              <div className="grid gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#706762]">Provinsi</Label>
                  <select value={data.province} onChange={(e) => { setField("province", e.target.value); setField("city", ""); setField("district", ""); }} className="mt-1.5 w-full h-11 px-3 rounded-lg border border-[#E5D9C4] bg-white text-sm" data-testid="loc-province">
                    {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#706762]">Kota / Kabupaten</Label>
                  <select value={data.city} onChange={(e) => { setField("city", e.target.value); setField("district", ""); }} className="mt-1.5 w-full h-11 px-3 rounded-lg border border-[#E5D9C4] bg-white text-sm" data-testid="loc-city">
                    <option value="">Pilih kota/kabupaten</option>
                    {(CITIES_BY_PROVINCE[data.province] || []).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#706762]">Kecamatan</Label>
                  <select value={data.district} onChange={(e) => setField("district", e.target.value)} className="mt-1.5 w-full h-11 px-3 rounded-lg border border-[#E5D9C4] bg-white text-sm" data-testid="loc-district">
                    <option value="">{DISTRICTS_BY_CITY[data.city] ? "Pilih kecamatan" : "Ketik manual jika tidak tersedia"}</option>
                    {(DISTRICTS_BY_CITY[data.city] || []).map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  {!DISTRICTS_BY_CITY[data.city] && data.city && (
                    <Input className="mt-2 h-11 border-[#E5D9C4]" placeholder="Nama kecamatan..." value={data.district} onChange={(e) => setField("district", e.target.value)}/>
                  )}
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#706762]">Venue (opsional)</Label>
                  <Input className="mt-1.5 h-11 border-[#E5D9C4] bg-white" placeholder="Nama gedung / area..." value={data.venue} onChange={(e) => setField("venue", e.target.value)} data-testid="loc-venue"/>
                </div>
              </div>
            </StepBlock>
          )}

          {step === "customer" && (
            <StepBlock title="Lengkapi data acara">
              <div className="grid gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#706762]">Nama Lengkap</Label>
                  <Input className="mt-1.5 h-11 border-[#E5D9C4] bg-white text-base" value={data.name} onChange={(e) => setField("name", e.target.value)} data-testid="cust-name" placeholder="Nama pengantin / pemesan"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#706762]">Nomor WhatsApp</Label>
                  <Input type="tel" className="mt-1.5 h-11 border-[#E5D9C4] bg-white text-base" value={data.whatsapp} onChange={(e) => setField("whatsapp", e.target.value)} data-testid="cust-wa" placeholder="08xxxxxxxxxx"/>
                  <div className="text-[10px] text-[#706762] mt-1">Agar tim sales bisa menghubungi Anda kembali.</div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#706762]">Email <span className="text-[10px] opacity-60">(opsional)</span></Label>
                  <Input type="email" className="mt-1.5 h-11 border-[#E5D9C4] bg-white text-base" value={data.email} onChange={(e) => setField("email", e.target.value)} data-testid="cust-email" placeholder="opsional"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#706762]">Tanggal Acara</Label>
                  <input type="date" className="mt-1.5 w-full h-11 px-3 rounded-lg border border-[#E5D9C4] bg-white text-base" value={data.event_date} onChange={(e) => setField("event_date", e.target.value)} data-testid="cust-date"/>
                </div>
              </div>
            </StepBlock>
          )}

          {step === "result" && rec && (
            <div className="text-center" data-testid="result-block">
              {rec.image && !rec.items && <img src={rec.image} alt={rec.name} className="w-full max-w-md mx-auto h-48 object-cover rounded-xl mb-4"/>}
              <CalendarHeart className="w-10 h-10 text-[#8B262D] mx-auto mb-3"/>
              <div className="text-[10px] uppercase tracking-widest text-[#8B262D] font-semibold">{rec.items ? "Total Sementara" : "Rekomendasi Untuk Kamu"}</div>
              <h3 className="font-serif text-4xl mt-2 text-[#1F1A19]" data-testid="result-package">{rec.name}</h3>
              {rec.brand && <div className="text-xs italic text-[#706762] mt-1">{rec.brand}</div>}
              <div className="text-xs text-[#706762] mt-2">{rec.price > 0 && !rec.items ? "Mulai dari" : rec.items ? "" : ""}</div>
              <div className="text-2xl font-semibold text-[#8B262D]" data-testid="result-price">{rec.price > 0 ? fmtIDR(rec.price) : "—"}</div>
              {rec.note && <div className="text-sm text-[#706762] mt-2 italic max-w-sm mx-auto">{rec.note}</div>}

              {rec.items?.length > 0 && (
                <div className="mt-6 text-left bg-[#F6F0EC] rounded-xl p-4 border border-[#E5D9C4]" data-testid="result-items">
                  <div className="text-[10px] uppercase tracking-widest text-[#8B5A2A] font-semibold mb-2">Rincian Paket Custom</div>
                  <div className="space-y-2">
                    {rec.items.map((p, i) => (
                      <div key={p.product_id || i} className="flex justify-between items-start gap-3 text-sm border-b border-[#E5D9C4] pb-2 last:border-none last:pb-0">
                        <div className="flex-1">
                          <div className="font-medium text-[#1F1A19]">{p.name}</div>
                          {p.brand && <div className="text-[11px] text-[#706762] italic">{p.brand}</div>}
                        </div>
                        <div className="font-mono text-[#8B262D] font-semibold shrink-0">{fmtIDR(p.price)}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center mt-3 pt-3 border-t-2 border-[#8B262D]">
                    <div className="text-xs uppercase tracking-widest text-[#8B262D] font-semibold">Total Sementara</div>
                    <div className="font-serif text-xl font-bold text-[#8B262D]">{fmtIDR(rec.price)}</div>
                  </div>
                </div>
              )}

              <div className="mt-6 text-left text-sm bg-[#F6F0EC] rounded-xl p-4 space-y-1.5">
                <Row k="Nama" v={data.name} />
                <Row k="Tanggal Acara" v={data.event_date} />
                <Row k="Layanan" v={data.layanan === "all_package" ? "Paket All Package (Wedding DWG)" : data.layanan === "satuan" ? (rec.items ? "Vendor Satuan — Paket Custom" : `Vendor Satuan — ${VENDORS.find(v=>v.slug===data.vendor_slug)?.name || ""}`) : ""} />
                {data.paket_type && <Row k="Jenis Paket" v={PAKET_TYPES.find(pt=>pt.key===data.paket_type)?.title} />}
                {data.guests && <Row k="Jumlah Tamu" v={data.guests} />}
                {data.budget && !rec.items && <Row k="Budget" v={data.budget} />}
                <Row k="Lokasi" v={[data.district, data.city, data.province].filter(Boolean).join(", ")} />
                {data.venue && <Row k="Venue" v={data.venue} />}
              </div>

              <div className="mt-6 flex flex-col md:flex-row gap-2">
                <a href={waHref} target="_blank" rel="noopener noreferrer" onClick={trackWA}
                  className="flex-1 h-12 bg-[#25D366] hover:bg-[#128C7E] text-white rounded-full inline-flex items-center justify-center font-semibold text-sm"
                  data-testid="btn-wa-result"><MessageCircle className="w-4 h-4 mr-1.5"/> WhatsApp Admin</a>
                <Button variant="outline" className="flex-1 h-12 rounded-full border-[#8B262D] text-[#8B262D]" onClick={() => { setStep("layanan"); setRec(null); }} data-testid="btn-edit-answers">Ubah Jawaban</Button>
              </div>
            </div>
          )}

          {step !== "result" && (
            <div className="mt-6 flex gap-2">
              {step !== "layanan" && !(hasProduct && step === "guests") && <Button variant="outline" className="flex-1 h-11 rounded-full" onClick={back} data-testid="btn-back"><ArrowLeft className="w-4 h-4 mr-1"/> Kembali</Button>}
              <Button className="flex-[2] h-11 bg-[#8B262D] hover:bg-[#6d1d23] text-white rounded-full disabled:opacity-40" onClick={next} disabled={!canContinue()} data-testid="btn-continue">
                Lanjut <ArrowRight className="w-4 h-4 ml-1"/>
              </Button>
            </div>
          )}
        </div>
      </div>

      {rec && floatVisible && (
        <div className="fixed bottom-3 left-3 right-3 md:left-auto md:right-6 md:bottom-6 md:w-80 bg-[#1F1A19] text-white rounded-xl p-4 shadow-2xl z-50" data-testid="float-recommendation">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-[#B87C80]">Rekomendasi kamu</div>
              <div className="font-serif text-lg mt-0.5">{rec.name}</div>
              <div className="text-[11px] text-[#B87C80] mt-1">{fmtIDR(rec.price)}</div>
            </div>
            <button onClick={() => { setFloatVisible(false); setFloatChip(true); }} className="text-white/60 hover:text-white" data-testid="float-close"><X className="w-4 h-4"/></button>
          </div>
          <a href={waHref} target="_blank" rel="noopener noreferrer" onClick={trackWA}
            className="mt-3 w-full h-9 rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white text-xs font-semibold flex items-center justify-center gap-1.5"
            data-testid="float-wa"><MessageCircle className="w-3.5 h-3.5"/> WhatsApp Admin</a>
        </div>
      )}
      {rec && !floatVisible && floatChip && (
        <button onClick={() => { setFloatVisible(true); setFloatChip(false); }} className="fixed bottom-4 right-4 bg-[#8B262D] text-white rounded-full px-4 py-2 text-xs font-semibold shadow-lg z-50 flex items-center gap-1.5" data-testid="float-chip">
          <Star className="w-3.5 h-3.5"/> Rekomendasi
        </button>
      )}
      <PublicFooter />
    </div>
  );
}

const StepBlock = ({ title, hint, children }) => (
  <div>
    <h2 className="font-serif text-2xl md:text-3xl text-[#1F1A19] mb-1">{title}</h2>
    {hint && <div className="text-xs text-[#706762] mb-4">{hint}</div>}
    <div className="mt-4">{children}</div>
  </div>
);
const ChoiceRow = ({ active, onClick, label, testId }) => (
  <button onClick={onClick} className={`p-3.5 rounded-xl border text-left transition-all ${active ? "bg-[#8B262D] text-white border-[#8B262D]" : "bg-white border-[#E5D9C4] hover:border-[#B87C80]"}`} data-testid={testId}>
    <div className={`text-sm font-medium ${active ? "text-white" : "text-[#1F1A19]"}`}>{label}</div>
  </button>
);
const Row = ({ k, v }) => v ? <div className="flex justify-between gap-3 text-xs"><span className="text-[#706762]">{k}</span><span className="text-[#1F1A19] font-medium text-right">{v}</span></div> : null;
